/**
 * Reusable table component for displaying sync operations
 * 
 * Used by both sync history and active syncs pages with customizable features
 */

import React, { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  LinearProgress,
  Box,
  Typography,
  Tooltip,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Alert
} from '@mui/material'
import {
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as PendingIcon,
  PlayArrow as RunningIcon,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  Stop as CancelledIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { SyncOperation } from '../services/api'
import { poolwerxColors } from '../theme/colors'
import { formatNumber } from '../utils/format'
import { getSystemIcon } from './SystemIcons'
import SyncProgressDisplay from './SyncProgressDisplay'
import { formatInUserTZ } from '../utils/timezoneUtils'
import ConfirmDialog from './shared/dialogs/ConfirmDialog'

interface SyncOperationsTableProps {
  operations: SyncOperation[]
  isLoading?: boolean
  showBulkActions?: boolean
  showProgress?: boolean
  onCancelOperation?: (operationId: string) => void
  onRefreshOperation?: (operationId: string) => void
  onSelectionChange?: (selectedOperations: string[]) => void
  emptyMessage?: string
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
  onSort?: (column: string) => void
}

interface ExpandedRow {
  [operationId: string]: boolean
}

const getStatusIcon = (status: string, hasError?: boolean) => {
  switch (status) {
    case 'pending':
      return <PendingIcon color="info" />
    case 'running':
      return <RunningIcon sx={{ color: poolwerxColors.teal }} />
    case 'completed':
      return hasError ? <WarningIcon color="warning" /> : <CompletedIcon color="success" />
    case 'failed':
      return <ErrorIcon color="error" />
    case 'cancelled':
      return <CancelledIcon color="disabled" />
    default:
      return <PendingIcon color="disabled" />
  }
}

/**
 * Get status chip color based on operation status and heartbeat staleness
 * For running operations, the color reflects heartbeat health:
 * - success (green): healthy heartbeat
 * - warning (orange): aging heartbeat
 * - error (red): stale heartbeat or no heartbeat (likely stuck)
 */
const getStatusColor = (status: string, hasError?: boolean, heartbeatStatus?: string | null, hasHeartbeat?: boolean): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'pending':
      return 'default'
    case 'running':
      // No lock = zombie sync (lock cleaned up but operation still "running")
      if (heartbeatStatus === 'no_lock') return 'error'
      // No heartbeat data = likely stuck
      if (hasHeartbeat === false) return 'error'
      // Color based on heartbeat health
      if (heartbeatStatus === 'stale') return 'error'
      if (heartbeatStatus === 'aging') return 'warning'
      return 'success'  // healthy heartbeat
    case 'completed':
      return hasError ? 'warning' : 'success'
    case 'failed':
      return 'error'
    case 'cancelled':
      return 'default'
    default:
      return 'default'
  }
}

/**
 * Format heartbeat age in compact human-readable form
 */
const formatHeartbeatAge = (seconds?: number): string => {
  if (seconds === undefined || seconds === null) return ''
  const minutes = Math.floor(seconds / 60)
  if (minutes < 1) return '<1m'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMins = minutes % 60
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`
}

const getSystemIconForTable = (system: string) => {
  const icon = getSystemIcon(system, { size: 20 })
  return icon || <RefreshIcon color="disabled" />
}

// Format memory in MB/GB based on size
const formatMemory = (mb: number): string => {
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`
  }
  return `${Math.round(mb)} MB`
}

const formatDuration = (seconds?: number): string => {
  if (!seconds) return '-'

  const totalMinutes = Math.floor(seconds / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const remainingSeconds = Math.floor(seconds % 60)

  if (hours > 0) {
    // 60+ minutes: show hours and minutes only (no seconds)
    return `${hours}h ${minutes}m`
  }
  if (totalMinutes > 0) {
    return `${totalMinutes}m ${remainingSeconds}s`
  }
  return `${remainingSeconds}s`
}

const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '-'

  try {
    // Use the new timezone utilities to format in user's timezone
    return formatInUserTZ(dateString, {
      format: 'dd MMM yyyy, hh:mm a',
      includeTimezone: false, // Don't include timezone in table cells to save space
      fallback: '-'
    })
  } catch (error) {
    console.error('Error formatting date:', error)
    return '-'
  }
}

const formatDateTimeWithTZ = (dateString?: string): string => {
  if (!dateString) return '-'

  try {
    // Include timezone for tooltips or detailed views
    return formatInUserTZ(dateString, {
      format: 'dd MMM yyyy, hh:mm a',
      includeTimezone: true,
      fallback: '-'
    })
  } catch (error) {
    console.error('Error formatting date:', error)
    return '-'
  }
}

const formatTimeAgo = (dateString?: string): string => {
  if (!dateString) return '-'

  try {
    const date = new Date(dateString)
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date'
    }

    const result = formatDistanceToNow(date, { addSuffix: true })
    // Capitalize first letter
    return result.charAt(0).toUpperCase() + result.slice(1)
  } catch {
    return 'Invalid date'
  }
}

export const SyncOperationsTable: React.FC<SyncOperationsTableProps> = ({
  operations,
  isLoading = false,
  showBulkActions = false,
  showProgress = true,
  onCancelOperation,
  onRefreshOperation,
  onSelectionChange,
  emptyMessage = 'No sync operations found',
  sortBy,
  sortDirection = 'desc',
  onSort
}) => {
  const [selectedOperations, setSelectedOperations] = useState<string[]>([])
  const [expandedRows, setExpandedRows] = useState<ExpandedRow>({})
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOperation, setSelectedOperation] = useState<string | null>(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [operationToCancel, setOperationToCancel] = useState<string | null>(null)

  // Sort operations: running first (by started_at desc), then pending (by created_at asc = FIFO queue)
  const sortedOperations = useMemo(() => {
    return [...operations].sort((a, b) => {
      // Running operations first
      if (a.status === 'running' && b.status !== 'running') return -1
      if (b.status === 'running' && a.status !== 'running') return 1

      // Both running: sort by started_at descending (most recent first)
      if (a.status === 'running' && b.status === 'running') {
        return new Date(b.started_at || b.created_at).getTime() -
               new Date(a.started_at || a.created_at).getTime()
      }

      // Pending: sort by created_at ascending (oldest first = next to start)
      if (a.status === 'pending' && b.status === 'pending') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      }

      // Pending before other statuses
      if (a.status === 'pending') return -1
      if (b.status === 'pending') return 1

      // Other statuses: by created_at descending
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
  }, [operations])

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = operations.map(op => op.operation_id)
      setSelectedOperations(newSelected)
      onSelectionChange?.(newSelected)
    } else {
      setSelectedOperations([])
      onSelectionChange?.([])
    }
  }

  const handleSelectOperation = (operationId: string) => {
    const selectedIndex = selectedOperations.indexOf(operationId)
    let newSelected: string[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedOperations, operationId)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedOperations.slice(1))
    } else if (selectedIndex === selectedOperations.length - 1) {
      newSelected = newSelected.concat(selectedOperations.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedOperations.slice(0, selectedIndex),
        selectedOperations.slice(selectedIndex + 1)
      )
    }

    setSelectedOperations(newSelected)
    onSelectionChange?.(newSelected)
  }

  const handleExpandRow = (operationId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [operationId]: !prev[operationId]
    }))
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, operationId: string) => {
    setAnchorEl(event.currentTarget)
    setSelectedOperation(operationId)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedOperation(null)
  }

  const handleCancel = () => {
    if (selectedOperation) {
      setOperationToCancel(selectedOperation)
    }
    setCancelDialogOpen(true)
    handleMenuClose()
  }

  const confirmCancel = () => {
    if (operationToCancel && onCancelOperation) {
      onCancelOperation(operationToCancel)
    }
    setCancelDialogOpen(false)
    setOperationToCancel(null)
  }

  const handleRefresh = () => {
    if (selectedOperation && onRefreshOperation) {
      onRefreshOperation(selectedOperation)
    }
    handleMenuClose()
  }

  const isSelected = (operationId: string) => selectedOperations.indexOf(operationId) !== -1

  if (operations.length === 0 && !isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          {emptyMessage}
        </Typography>
      </Paper>
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {showBulkActions && (
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedOperations.length > 0 && selectedOperations.length < operations.length}
                  checked={operations.length > 0 && selectedOperations.length === operations.length}
                  onChange={handleSelectAll}
                  disabled={isLoading}
                />
              </TableCell>
            )}
            <TableCell>Status</TableCell>
            <TableCell>Franchise</TableCell>
            <TableCell>System</TableCell>
            {showProgress && <TableCell sx={{ width: '200px', minWidth: '200px' }}>Progress</TableCell>}
            <TableCell>
              <TableSortLabel
                active={sortBy === 'created_at'}
                direction={sortBy === 'created_at' ? sortDirection : 'asc'}
                onClick={() => onSort?.('created_at')}
              >
                Started
              </TableSortLabel>
            </TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Memory</TableCell>
            <TableCell>Last Update</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedOperations.map((operation) => (
            <React.Fragment key={operation.operation_id}>
              <TableRow
                hover
                selected={isSelected(operation.operation_id)}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                {showBulkActions && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected(operation.operation_id)}
                      onChange={() => handleSelectOperation(operation.operation_id)}
                      disabled={isLoading}
                    />
                  </TableCell>
                )}
                
                <TableCell>
                  <Tooltip
                    title={
                      operation.status === 'completed' && operation.error_message
                        ? 'Completed with errors - expand for details'
                        : operation.status === 'running' && operation.heartbeat_status === 'no_lock'
                        ? 'No active lock - zombie sync (lock expired but operation still running)'
                        : operation.status === 'running' && operation.heartbeat_age_seconds == null
                        ? 'No lock heartbeat - sync may be stuck (lock cleaned up)'
                        : operation.status === 'running' && operation.heartbeat_status === 'stale'
                        ? 'Lock heartbeat stale - sync may be stuck or worker crashed'
                        : operation.status === 'running' && operation.heartbeat_status === 'aging'
                        ? 'Lock heartbeat aging - monitor closely'
                        : ''
                    }
                  >
                    <Chip
                      label={operation.status === 'completed' && operation.error_message ? 'COMPLETED (ERRORS)' : operation.status.toUpperCase()}
                      color={getStatusColor(
                        operation.status,
                        !!(operation.status === 'completed' && operation.error_message),
                        operation.heartbeat_status,
                        operation.heartbeat_age_seconds != null
                      )}
                      size="small"
                    />
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {operation.franchise_name}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getSystemIconForTable(operation.sync_system)}
                    <Typography variant="body2" textTransform="capitalize">
                      {operation.sync_system}
                    </Typography>
                  </Box>
                </TableCell>

                {showProgress && (
                  <TableCell sx={{ width: '200px', minWidth: '200px' }}>
                    {operation.status === 'running' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress
                          size={16}
                          thickness={5}
                          sx={{
                            color: poolwerxColors.teal,
                            flexShrink: 0
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <SyncProgressDisplay
                            progress={operation.progress_percentage || 0}
                            currentStep={operation.current_step || 'Initializing...'}
                            status={operation.status}
                            system={operation.sync_system as 'lightspeed' | 'xero' | 'skimmer'}
                            showDetails={false}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {operation.status === 'pending' ? 'Queued' : 'N/A'}
                      </Typography>
                    )}
                  </TableCell>
                )}

                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {operation.started_at ? formatDateTime(operation.started_at) : '-'}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {formatDuration(
                      operation.elapsed_seconds || operation.duration_seconds
                    )}
                  </Typography>
                </TableCell>

                <TableCell>
                  {operation.status === 'running' && operation.worker_memory_mb ? (
                    <Typography variant="body2">
                      {formatMemory(operation.worker_memory_mb)}
                    </Typography>
                  ) : operation.memory_end_mb ? (
                    <Tooltip title={`Start: ${formatMemory(operation.memory_start_mb || 0)} → End: ${formatMemory(operation.memory_end_mb)} (${operation.memory_delta_mb && operation.memory_delta_mb >= 0 ? '+' : ''}${formatMemory(operation.memory_delta_mb || 0)})`}>
                      <Typography variant="body2">
                        {formatMemory(operation.memory_end_mb)}
                        {operation.memory_delta_mb !== undefined && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 0.5, color: operation.memory_delta_mb > 50 ? 'warning.main' : 'text.secondary' }}
                          >
                            ({operation.memory_delta_mb >= 0 ? '+' : ''}{Math.round(operation.memory_delta_mb)})
                          </Typography>
                        )}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="textSecondary">-</Typography>
                  )}
                </TableCell>

                <TableCell>
                  {operation.status === 'running' ? (
                    <Tooltip
                      title={
                        operation.heartbeat_status === 'no_lock'
                          ? 'No active lock - zombie sync (lock expired)'
                          : operation.heartbeat_age_seconds == null
                          ? 'No lock heartbeat - sync may be stuck'
                          : operation.heartbeat_status === 'stale'
                          ? 'Lock heartbeat stale - sync may be stuck or worker crashed'
                          : operation.heartbeat_status === 'aging'
                          ? 'Lock heartbeat aging - monitor closely'
                          : 'Lock heartbeat healthy - sync actively running'
                      }
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: operation.heartbeat_status === 'no_lock' || operation.heartbeat_age_seconds == null
                            ? poolwerxColors.error || '#f44336'
                            : operation.heartbeat_status === 'stale'
                            ? poolwerxColors.error || '#f44336'
                            : operation.heartbeat_status === 'aging'
                            ? poolwerxColors.warning || '#ff9800'
                            : 'text.secondary',
                          fontWeight: operation.heartbeat_status === 'no_lock' || operation.heartbeat_age_seconds == null || operation.heartbeat_status !== 'healthy' ? 600 : 400
                        }}
                      >
                        {operation.heartbeat_status === 'no_lock'
                          ? 'No lock ⚠️'
                          : operation.heartbeat_age_seconds == null
                          ? 'No heartbeat ⚠️'
                          : `${formatHeartbeatAge(operation.heartbeat_age_seconds)} ago${operation.heartbeat_status === 'stale' ? ' ⚠️' : ''}`
                        }
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      -
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleExpandRow(operation.operation_id)}
                    >
                      {expandedRows[operation.operation_id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, operation.operation_id)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>

              {/* Expanded row details */}
              <TableRow>
                <TableCell colSpan={showBulkActions ? 10 : 9} sx={{ p: 0 }}>
                  <Collapse in={expandedRows[operation.operation_id]} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                      <Typography variant="h6" gutterBottom>
                        Operation Details
                      </Typography>

                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Operation ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {operation.operation_id}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Operation Type
                          </Typography>
                          <Typography variant="body2">
                            {operation.operation_type}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Initiated By
                          </Typography>
                          <Typography variant="body2">
                            {operation.initiated_by || 'System'}
                          </Typography>
                          {operation.started_at && (
                            <Typography variant="body2" color="text.secondary">
                              ({formatDateTime(operation.started_at)})
                            </Typography>
                          )}
                        </Box>

                        {/* Only show record counts for completed operations */}
                        {operation.status === 'completed' && (
                          <>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Records Created
                              </Typography>
                              <Typography variant="body2">
                                {formatNumber(operation.records_created)}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Records Updated
                              </Typography>
                              <Typography variant="body2">
                                {formatNumber(operation.records_updated)}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Records Deleted
                              </Typography>
                              <Typography variant="body2">
                                {formatNumber(operation.records_deleted)}
                              </Typography>
                            </Box>
                          </>
                        )}

                        {operation.completed_at && (
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              Completed
                            </Typography>
                            <Typography variant="body2">
                              {formatDateTime(operation.completed_at)}
                            </Typography>
                          </Box>
                        )}

                        {/* Show current step for running operations */}
                        {operation.status === 'running' && (
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              Current Step
                            </Typography>
                            <Typography variant="body2">
                              {operation.current_step}
                            </Typography>
                          </Box>
                        )}

                        {/* Show records processed for running operations */}
                        {operation.status === 'running' && operation.records_processed > 0 && (
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              Records Processed
                            </Typography>
                            <Typography variant="body2">
                              {formatNumber(operation.records_processed)}
                              {operation.records_total && ` of ${formatNumber(operation.records_total)}`}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Entity Breakdown - only show for completed operations with data */}
                      {operation.status === 'completed' && operation.sync_metadata && Object.keys(operation.sync_metadata).length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }} gutterBottom>
                            Entity Breakdown
                          </Typography>
                          {Object.entries(operation.sync_metadata).map(([system, dataTypes]) => {
                            // Check if there's any data to show
                            const hasAnyData = Object.values(dataTypes).some((counts: any) =>
                              counts.created > 0 || counts.updated > 0 || counts.deleted > 0
                            )
                            if (!hasAnyData) return null

                            return (
                              <Box key={system} sx={{ ml: 1, mt: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize', color: 'text.secondary', mb: 0.5 }}>
                                  {system}
                                </Typography>
                                <Box sx={{ ml: 2 }}>
                                  {Object.entries(dataTypes).map(([dataType, counts]: [string, any]) => {
                                    const hasData = counts.created > 0 || counts.updated > 0 || counts.deleted > 0
                                    if (!hasData) return null

                                    // Get display name and emoji from metadata or fallback
                                    const displayName = counts.display_name || dataType.replace(/_/g, ' ').split(' ')
                                      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                      .join(' ')
                                    const emoji = counts.emoji || '🔄'

                                    // Build a compact summary of changes
                                    const changes = []
                                    if (counts.created > 0) changes.push(`${formatNumber(counts.created)} created`)
                                    if (counts.updated > 0) changes.push(`${formatNumber(counts.updated)} updated`)
                                    if (counts.deleted > 0) changes.push(`${formatNumber(counts.deleted)} deleted`)

                                    return (
                                      <Box key={dataType} sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        py: 0.5,
                                        gap: 2,
                                        '&:hover': { backgroundColor: 'action.hover', borderRadius: 1, px: 1, mx: -1 }
                                      }}>
                                        <Typography variant="body2" sx={{
                                          minWidth: 160,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5
                                        }}>
                                          <span>{emoji}</span>
                                          <span style={{ fontWeight: 500 }}>{displayName}</span>
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {changes.join(' • ')}
                                        </Typography>
                                      </Box>
                                    )
                                  })}
                                </Box>
                              </Box>
                            )
                          })}
                        </Box>
                      )}

                      {operation.error_message && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            {operation.error_message}
                          </Typography>
                        </Alert>
                      )}

                      {/* Remove the Progress Details section for running operations since it duplicates info */}
                      {/* The progress is already shown in the main table and current step is shown above */}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      {/* Action menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        disablePortal={true}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        disableRestoreFocus={false}
      >
        {onCancelOperation && selectedOperation && (
          <MenuItem 
            onClick={handleCancel}
            disabled={!selectedOperation || !['pending', 'running'].includes(
              operations.find(op => op.operation_id === selectedOperation)?.status || ''
            )}
          >
            <ListItemIcon>
              <CancelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cancel</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false)
          setOperationToCancel(null)
        }}
        onConfirm={confirmCancel}
        tone="warning"
        title="Cancel Sync Operation"
        confirmLabel="Confirm Cancel"
        body="Are you sure you want to cancel this sync operation? This action cannot be undone."
      />

      {isLoading && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
          <LinearProgress />
        </Box>
      )}
    </TableContainer>
  )
}

export default SyncOperationsTable